import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useColorTheme } from '../../context/ColorThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppSettings = ({ onBackToMain, sessionToken }) => {
  const { colorTheme } = useColorTheme();
  const [qrCode, setQrCode] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [serviceAvailable, setServiceAvailable] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Check if service is unavailable
      if (response.data.status === 'service_unavailable') {
        setServiceAvailable(false);
        setConnectionStatus('service_unavailable');
        return;
      }
      
      setServiceAvailable(true);
      setConnectionStatus(response.data.status);
      if (response.data.connected) {
        setMessage('✅ WhatsApp is connected!');
      }
    } catch (error) {
      console.error('Status check error:', error);
      // If we get a connection error, service might be unavailable
      if (error.code === 'ERR_NETWORK' || error.response?.status === 500) {
        setServiceAvailable(false);
        setConnectionStatus('service_unavailable');
      }
    }
  };

  const handleInitialize = async () => {
    setIsLoading(true);
    setError('');
    setMessage('');
    setQrCode(null);
    
    try {
      const response = await axios.post(`${API}/whatsapp/initialize`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      // Check for service unavailable response
      if (response.data.status === 'service_unavailable') {
        setServiceAvailable(false);
        setConnectionStatus('service_unavailable');
        setError(response.data.message || 'WhatsApp service is not available');
        return;
      }

      if (response.data.status === 'connected') {
        setConnectionStatus('connected');
        setMessage('✅ Already connected!');
      } else if (response.data.status === 'qr_ready') {
        setConnectionStatus('qr_ready');
        await fetchQRCode();
      } else if (response.data.status === 'timeout') {
        setError(response.data.message || 'Connection timed out. Please try again.');
      }
    } catch (error) {
      console.error('Initialize error:', error);
      if (error.response?.data?.status === 'service_unavailable') {
        setServiceAvailable(false);
        setConnectionStatus('service_unavailable');
        setError(error.response.data.message);
      } else {
        setError('Failed to initialize WhatsApp connection. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr-code`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.status === 'service_unavailable') {
        setServiceAvailable(false);
        return;
      }
      
      if (response.data.qr) {
        setQrCode(response.data.qr);
        setMessage('📱 Scan the QR code with your WhatsApp mobile app');
        pollConnection();
      }
    } catch (error) {
      console.error('QR fetch error:', error);
      setError('Failed to fetch QR code');
    }
  };

  const pollConnection = () => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/whatsapp/status`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        
        if (response.data.connected) {
          setConnectionStatus('connected');
          setMessage('✅ WhatsApp connected successfully!');
          setQrCode(null);
          clearInterval(interval);
        }
      } catch (error) {
        clearInterval(interval);
      }
    }, 2000);

    setTimeout(() => clearInterval(interval), 120000);
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp?')) {
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/whatsapp/logout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setConnectionStatus('disconnected');
      setQrCode(null);
      setMessage('✅ Disconnected successfully');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to disconnect');
    } finally {
      setIsLoading(false);
    }
  };

  // Render service unavailable UI
  if (!serviceAvailable || connectionStatus === 'service_unavailable') {
    return (
      <div className={`p-6 ${colorTheme === 'purple' ? 'bg-purple-50' : colorTheme === 'blue' ? 'bg-blue-50' : 'bg-green-50'} dark:bg-gray-900 min-h-screen`}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              📱 WhatsApp Integration
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Connect your personal WhatsApp to publish campaigns directly
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
                  WhatsApp Service Unavailable
                </h3>
                <div className="mt-2 text-yellow-700 dark:text-yellow-300">
                  <p className="mb-3">
                    The WhatsApp integration service is currently not available in this environment. This can happen due to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>The WhatsApp service may not be deployed in production</li>
                    <li>The service may be temporarily offline</li>
                    <li>Network connectivity issues</li>
                  </ul>
                </div>
                <div className="mt-4">
                  <button
                    onClick={checkStatus}
                    className={`px-4 py-2 ${
                      colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                      colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                      'bg-green-600 hover:bg-green-700'
                    } text-white rounded-lg transition-colors`}
                  >
                    Retry Connection
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Alternative Options
            </h3>
            <div className="space-y-3 text-gray-700 dark:text-gray-300">
              <p>While WhatsApp integration is unavailable, you can still:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Publish campaigns via <strong>Telegram</strong></li>
                <li>Export your campaign content and share manually</li>
                <li>Contact support if you need WhatsApp integration urgently</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${colorTheme === 'purple' ? 'bg-purple-50' : colorTheme === 'blue' ? 'bg-blue-50' : 'bg-green-50'} dark:bg-gray-900 min-h-screen`}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            📱 WhatsApp Integration
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Connect your personal WhatsApp to publish campaigns directly
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Connection Status
          </h3>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'qr_ready' ? 'bg-yellow-500' :
                connectionStatus === 'checking' ? 'bg-blue-500 animate-pulse' :
                'bg-gray-400'
              }`}></div>
              <span className="text-gray-700 dark:text-gray-300">
                {connectionStatus === 'connected' ? 'Connected' :
                 connectionStatus === 'qr_ready' ? 'Waiting for scan' :
                 connectionStatus === 'checking' ? 'Checking status...' :
                 'Disconnected'}
              </span>
            </div>
            
            {connectionStatus === 'connected' ? (
              <button
                onClick={handleLogout}
                disabled={isLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400"
                data-testid="whatsapp-disconnect-btn"
              >
                {isLoading ? 'Disconnecting...' : 'Disconnect'}
              </button>
            ) : (
              <button
                onClick={handleInitialize}
                disabled={isLoading || connectionStatus === 'checking'}
                className={`px-4 py-2 ${
                  colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                  colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                  'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg disabled:bg-gray-400`}
                data-testid="whatsapp-connect-btn"
              >
                {isLoading ? 'Connecting...' : 'Connect WhatsApp'}
              </button>
            )}
          </div>

          {message && (
            <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg">
              {message}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {qrCode && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Scan QR Code with WhatsApp
            </h3>
            <div className="flex justify-center">
              <QRCodeSVG value={qrCode} size={256} level="H" />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-4">
              Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
            </p>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            How to Use
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>Click "Connect WhatsApp" above</li>
            <li>Scan the QR code with your WhatsApp mobile app</li>
            <li>Once connected, you can publish campaigns from the campaign details page</li>
            <li>You can send to multiple numbers or WhatsApp groups</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
